import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { useEffect, useMemo } from "react";
import { graphql, useQueryLoader } from "react-relay";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { notEmpty } from "shared/lib/utils";

import { DefaultPageSize } from "scmp-app/components/search/consts";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { sanitizeKeyword } from "scmp-app/components/search/helpers";
import { ExperimentTitleNames } from "scmp-app/lib/optimizely/consts";
import { useOptimizelyContext } from "scmp-app/lib/optimizely/context";
import { useActiveExperiment } from "scmp-app/lib/optimizely/hooks";
import type { searchResultPanelQuery } from "scmp-app/queries/__generated__/searchResultPanelQuery.graphql";

import { SearchResultList } from "./search-result-list";
import { StyledLoading } from "./styles";

export const query = graphql`
  query searchResultPanelQuery(
    $after: String
    $articleTypeIds: [String!]
    $contentTypes: [ContentTypeEnum!]
    $endDate: Timestamp
    $first: Int = 10
    $orderBy: ArticlesSearchOrderByInput
    $paywallTypeIds: [String!]
    $query: String!
    $sectionIds: [String!]
    $startDate: Timestamp
    $variant: Int
  ) {
    ...searchResultListSearchResultsQuery
      @arguments(
        after: $after
        articleTypeIds: $articleTypeIds
        contentTypes: $contentTypes
        endDate: $endDate
        first: $first
        orderBy: $orderBy
        paywallTypeIds: $paywallTypeIds
        query: $query
        sectionIds: $sectionIds
        startDate: $startDate
        variant: $variant
      )
  }
`;

export const SearchResultPanel: FunctionComponent = () => {
  const { query: routerQuery } = useRouter();
  const {
    articleResultIdMap,
    articleTypeIds,
    contentTypes,
    currentValue,
    dateRange,
    orderBy,
    paywallTypeIds,
    sectionIds,
  } = useSearchContext();
  const { initialized } = useOptimizelyContext();
  const { experiment } = useActiveExperiment(ExperimentTitleNames.NewSearchBackend);
  const [queryReference, loadQuery, disposeQuery] = useQueryLoader<searchResultPanelQuery>(query);

  const variant = useMemo(() => {
    if (experiment?.variation.name === undefined) return 0;
    return Number.parseInt(experiment?.variation.name, 10);
  }, [experiment?.variation.name]);

  useEffect(() => {
    if (!notEmpty(currentValue)) return;
    if (sanitizeKeyword(routerQuery.q as string) !== currentValue) return;
    if (!initialized) return;

    articleResultIdMap?.clear();

    window.dataLayer?.push({
      event: "experience_impression",
      exp_variant_string: `manual-search001-${variant}`,
      Holdback: false,
    });

    loadQuery(
      {
        articleTypeIds,
        contentTypes,
        first: DefaultPageSize,
        orderBy,
        paywallTypeIds,
        query: currentValue,
        sectionIds,
        variant,
        ...dateRange,
      },
      { fetchPolicy: "network-only" },
    );

    return () => {
      disposeQuery();
    };
  }, [
    articleResultIdMap,
    articleTypeIds,
    contentTypes,
    currentValue,
    dateRange,
    disposeQuery,
    initialized,
    loadQuery,
    orderBy,
    paywallTypeIds,
    routerQuery.q,
    sectionIds,
    variant,
  ]);

  if (!notEmpty(currentValue)) return null;

  return (
    <>
      {initialized ? (
        <ClientSideSuspense fallback={<StyledLoading />}>
          {queryReference && <SearchResultList reference={queryReference} />}
        </ClientSideSuspense>
      ) : (
        <StyledLoading />
      )}
    </>
  );
};

SearchResultPanel.displayName = "SearchResultPanel";
